/**
 * useSessionLineageEvents
 *
 * Builds the lineage timeline event list for a given prioritization session.
 * Reads from sessionStore, trackStore, degapProcessStore, and
 * sessionArtifactsStore (for transcript/video attachment events).
 *
 * Events derived:
 *   – session_created          : when the session was created
 *   – cycle_added              : parent cycle context (crosswise)
 *   – session_tool_selected    : when a prioritization tool was chosen
 *   – session_catchball_seeded : when seeded from a catchball output
 *   – session_run_started      : when the run began
 *   – session_run_completed    : when the run was completed
 *   – session_results_published: when results were published
 *   – artifact_transcript      : each attached AI transcript
 *   – artifact_video           : each attached video recording
 *   – artifact_document        : each attached document link
 */

import { useMemo } from 'react'
import { useSessionStore } from '../../../stores/prioritization/sessionStore'
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore'
import { useTrackStore } from '../../../stores/prioritization/trackStore'
import { useSessionArtifactsStore } from '../../../stores/prioritization/sessionArtifactsStore'
import type { PrioritizationSession } from '../../../types/prioritization/session'
import { toTimelineDate, type TimelineEvent, type LineageEventType } from './LineageTimeline'

function td(v: unknown) {
  return toTimelineDate(v)
}

/**
 * Session rows can carry legacy/snake_case timestamp fields (from raw DB rows)
 * that are not part of the canonical PrioritizationSession type. Model them
 * explicitly so the timeline can read them without `any`.
 */
type SessionLineageSource = PrioritizationSession & {
  cycle_id?: string
  track_id?: string
  toolSelectedAt?: string | Date | null
  runStartedAt?: string | Date | null
  run_started_at?: string | Date | null
  completedAt?: string | Date | null
  run_completed_at?: string | Date | null
  results_published_at?: string | Date | null
}

const ARTIFACT_TYPE_TO_EVENT: Record<string, LineageEventType> = {
  ai_transcript: 'artifact_transcript',
  video_link: 'artifact_video',
  document_link: 'artifact_document',
}

export function useSessionLineageEvents(sessionId: string): TimelineEvent[] {
  const session = useSessionStore((s) =>
    s.sessions.find((sess) => sess.id === sessionId),
  )
  const allCycles = useDegapProcessStore((s) => s.cycles)
  const allTracks = useTrackStore((s) => s.tracks)
  // Select the full array (stable reference from Zustand) and filter in useMemo.
  // Inline .filter() inside a selector always returns a new array reference, which
  // causes useSyncExternalStore to see a perpetual "change" and loop infinitely.
  const allArtifacts = useSessionArtifactsStore((s) => s.artifacts)
  const artifacts = useMemo(
    () => allArtifacts.filter((a) => a.sessionId === sessionId),
    [allArtifacts, sessionId],
  )

  return useMemo(() => {
    if (!session) return []

    const events: TimelineEvent[] = []
    const s = session as SessionLineageSource

    // 1. Created
    const createdAt = td(s.createdAt)
    if (createdAt) {
      events.push({
        id: 'session-created',
        type: 'session_created',
        timestamp: createdAt,
        label: 'Session Created',
        detail: s.name,
        actor: s.createdBy ?? s.facilitatorId,
      })
    }

    // 2. Parent cycle context (crosswise)
    const cycleId = s.cycleId ?? s.cycle_id
    if (cycleId) {
      const cycle = allCycles.find((c) => c.id === cycleId)
      const ts = td(cycle?.createdAt) ?? createdAt ?? new Date()
      events.push({
        id: `parent-cycle-${cycleId}`,
        type: 'cycle_added',
        timestamp: ts,
        label: 'Cycle Context',
        detail: cycle?.name,
        linkedId: cycleId,
        linkedLabel: cycle?.name ?? 'View cycle',
        linkedPath: `/prioritization/cycles/${cycleId}`,
        isEstimated: true,
      })
    }

    // 3. Parent track context (crosswise)
    const trackId = s.trackId ?? s.track_id
    if (trackId) {
      const track = allTracks.find((t) => t.id === trackId)
      const ts = td(track?.createdAt) ?? createdAt ?? new Date()
      events.push({
        id: `parent-track-${trackId}`,
        type: 'track_created',
        timestamp: ts,
        label: 'Track',
        detail: track?.name,
        linkedId: trackId,
        linkedLabel: track?.name ?? 'View track',
        linkedPath: `/prioritization/tracks/${trackId}`,
        isEstimated: true,
      })
    }

    // 4. Catchball seeded
    if (s.seededFromCatchball && s.catchBallOutputId) {
      const ts = createdAt ?? new Date()
      events.push({
        id: `catchball-seeded-${s.catchBallOutputId}`,
        type: 'session_catchball_seeded',
        timestamp: ts,
        label: 'Seeded from Catchball',
        detail: `Output: ${String(s.catchBallOutputId).slice(0, 8)}`,
        isEstimated: !createdAt,
      })
    }

    // 5. Config session (inheritance chain)
    if (s.configSessionId) {
      const ts = createdAt ?? new Date()
      events.push({
        id: `config-session-${s.configSessionId}`,
        type: 'session_tool_selected',
        timestamp: ts,
        label: 'Config Session Inherited',
        detail: `Config: ${String(s.configSessionId).slice(0, 8)}`,
        linkedId: s.configSessionId,
        linkedLabel: 'View config session',
        linkedPath: `/prioritization/sessions/${s.configSessionId}`,
        isEstimated: !createdAt,
      })
    }

    // 6. Tool selected
    if (s.selectedToolId) {
      const ts = td(s.toolSelectedAt) ?? createdAt
      if (ts) {
        events.push({
          id: 'tool-selected',
          type: 'session_tool_selected',
          timestamp: ts,
          label: 'Tool Selected',
          detail: s.selectedToolId,
          actor: s.facilitatorId,
          isEstimated: !td(s.toolSelectedAt),
        })
      }
    }

    // 7. Run started
    const runStartedAt = td(s.runStartedAt ?? s.run_started_at)
    if (runStartedAt) {
      events.push({
        id: 'run-started',
        type: 'session_run_started',
        timestamp: runStartedAt,
        label: 'Run Started',
        actor: s.facilitatorId,
      })
    }

    // 8. Run completed
    const completedAt = td(s.completedAt ?? s.run_completed_at)
    if (completedAt) {
      events.push({
        id: 'run-completed',
        type: 'session_run_completed',
        timestamp: completedAt,
        label: 'Run Completed',
      })
    }

    // 9. Results published
    const publishedAt = td(s.publishedAt ?? s.results_published_at)
    if (publishedAt) {
      events.push({
        id: 'results-published',
        type: 'session_results_published',
        timestamp: publishedAt,
        label: 'Results Published',
        actor: s.publishedBy,
      })
    }

    // 10. Attached artifacts (transcript / video / document)
    artifacts.forEach((artifact) => {
      const ts = td(artifact.createdAt) ?? new Date()
      const eventType: LineageEventType =
        ARTIFACT_TYPE_TO_EVENT[artifact.artifactType] ?? 'artifact_document'
      events.push({
        id: `artifact-${artifact.id}`,
        type: eventType,
        timestamp: ts,
        label: artifact.title,
        detail: artifact.notes ?? undefined,
        actor: artifact.createdBy ?? undefined,
        linkedLabel: artifact.url ? 'Open link' : undefined,
        linkedPath: artifact.url ?? undefined,
      })
    })

    // Dedupe + sort
    const seen = new Set<string>()
    return events
      .filter((e) => {
        if (seen.has(e.id)) return false
        seen.add(e.id)
        return true
      })
      .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
  }, [session, allCycles, allTracks, artifacts])
}
